import { useState } from 'react';

import {
    Box,
    BoxProps,
    FormControl,
    InputBase,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
} from '@mui/material';
import { debounce, isEmpty } from '@npi/buddy-utils';
import { useField } from 'formik';
import { CountryCodes } from 'src/constants';
import { useCountries } from 'src/hooks';

interface ICustomInputProps {
    label?: string;
    setFieldValue: (field, country) => void;
    placeholder?: string;
    fieldName: string;
    boxStyling?: BoxProps;
    dataTestId?: string;
    disabled?: boolean;
    filterByAlpha2Codes?: Array<CountryCodes>;
}

const CountrySelectInput = ({
    label = 'Country',
    setFieldValue,
    placeholder = 'Enter/Search Country',
    fieldName,
    boxStyling,
    dataTestId = `${fieldName}`,
    disabled,
    filterByAlpha2Codes,
    ...props
}: ICustomInputProps) => {
    const [field, meta] = useField(fieldName);
    const { countries } = useCountries(filterByAlpha2Codes);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const handleDropdownChange = (event: SelectChangeEvent) => {
        const { value } = event.target;
        setFieldValue(fieldName, value);
    };

    const handleOpen = () => {
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const renderDropDownValue = (selected) => {
        const country = countries?.find((country) => {
            return country?.name?.toLowerCase() === selected?.toLowerCase();
        });

        const src = country?.flag;

        return (
            <span
                style={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {src && (
                    <img
                        src={src}
                        alt="country flag"
                        style={{
                            borderRadius: '50%',
                            height: '24px',
                            width: '24px',
                            objectFit: 'cover',
                            marginRight: '8px',
                        }}
                    />
                )}
                {selected}
            </span>
        );
    };

    const searchCountry = (input) => {
        const lowerCaseInput = input.toLowerCase();

        const country = countries.find((country) => {
            return country.name.toLowerCase() === lowerCaseInput;
        });

        if (country) {
            setFieldValue(fieldName, country.name);
        }

        return;
    };

    const debouncedSearchCountry = debounce(searchCountry, 1000);

    const handleInputChange = (event) => {
        const { value } = event.target;
        setFieldValue(fieldName, value);

        debouncedSearchCountry(value);
    };

    return (
        <Stack sx={{ position: 'relative' }}>
            <label className="mb-5 font-bold" data-testid={`${dataTestId}-label-value`}>
                {label}
            </label>
            <Box
                className={`country-select-input-base ${
                    meta.touched && meta.error ? 'input-error' : ''
                }`}
                {...boxStyling}
            >
                <FormControl sx={{ border: 'none' }}>
                    <Select
                        id="country select"
                        value={field.value || ''}
                        inputProps={{
                            'data-testid': `${dataTestId}-dropdown`,
                        }}
                        displayEmpty
                        onChange={handleDropdownChange}
                        onOpen={handleOpen}
                        onClose={handleClose}
                        open={isOpen}
                        renderValue={(e) => {
                            if (isEmpty(e)) {
                                return (
                                    <span
                                        style={{ opacity: 0.6 }}
                                        data-testid={`${dataTestId}-empty-value`}
                                    >
                                        {label}
                                    </span>
                                );
                            }
                            return renderDropDownValue(e);
                        }}
                        sx={{
                            '&:before': {
                                borderBottom: 'none',
                            },
                            '&:after': {
                                borderBottom: 'none',
                            },
                            '&:hover:not(.Mui-disabled):before': {
                                borderBottom: 'none',
                            },
                            '&.MuiSelect-select fieldset': {
                                border: 'none',
                            },

                            borderRadius: '0px',
                            border: 'none',

                            '&.MuiInputBase-root fieldset': {
                                border: 'none',
                            },
                            paddingLeft: '10px',
                        }}
                        disabled={disabled}
                    >
                        {countries &&
                            countries.map((country) => {
                                return (
                                    <MenuItem key={country.name} value={country.name}>
                                        <img
                                            src={country.flag}
                                            alt="country flag"
                                            style={{ maxWidth: '15px', marginRight: '5px' }}
                                        />
                                        {country.name}
                                    </MenuItem>
                                );
                            })}
                    </Select>
                </FormControl>
                <InputBase
                    {...field}
                    {...props}
                    inputProps={{
                        'data-testid': `${dataTestId}-input`,
                    }}
                    onChange={handleInputChange}
                    placeholder={placeholder}
                    autoComplete="off"
                    className={`search-input-base `}
                    disabled={disabled}
                />
            </Box>

            {meta.error && <label className="input-base-field-error">{meta.error}</label>}
        </Stack>
    );
};

export default CountrySelectInput;
